import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Chip,
  CircularProgress,
  useTheme,
  Divider,
  useMediaQuery,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import enLocale from 'date-fns/locale/en-GB';
import { fetchBillDetailPnl } from '../../services/backendAPI';
import TimelineIcon from '@mui/icons-material/Timeline';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { formatTimeToIST } from '../../utils/dateUtils';

const PnlHistoryDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Function to check if a date is a weekend (Saturday or Sunday)
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  };
  
  // Function to get the most recent business day
  const getMostRecentBusinessDay = () => {
    const today = new Date();
    let recentBusinessDay = new Date(today);
    
    // If today is Monday, set to previous Friday
    if (today.getDay() === 1) { // Monday
      recentBusinessDay.setDate(today.getDate() - 3);
      return recentBusinessDay;
    }
    
    // If today is Sunday, set to previous Friday
    if (today.getDay() === 0) { // Sunday
      recentBusinessDay.setDate(today.getDate() - 2);
      return recentBusinessDay;
    }
    
    // If today is Saturday, set to previous Friday
    if (today.getDay() === 6) { // Saturday
      recentBusinessDay.setDate(today.getDate() - 1);
      return recentBusinessDay;
    }
    
    // For regular weekdays, use yesterday
    recentBusinessDay.setDate(today.getDate() - 1);
    return recentBusinessDay;
  };
  
  // Table data state
  const [pnlHistory, setPnlHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Get dates from localStorage if available, otherwise use defaults
  const getInitialStartDate = () => {
    const savedDate = localStorage.getItem('pnl_history_startDate');
    if (savedDate) {
      return new Date(savedDate);
    }
    // Use today's date instead of 3 months lookback
    return new Date();
  };
  
  const getInitialEndDate = () => {
    const savedDate = localStorage.getItem('pnl_history_endDate');
    if (savedDate) {
      return new Date(savedDate);
    }
    // Use today's date instead of most recent business day
    return new Date();
  };
  
  // Get date 3 months before the given date
  const getThreeMonthsBefore = (date) => {
    const threeMonthsBefore = new Date(date);
    threeMonthsBefore.setMonth(date.getMonth() - 3);
    return threeMonthsBefore;
  };
  
  const getInitialExchange = () => {
    return localStorage.getItem('pnl_history_exchange') || 'NFO';
  };
  
  // Filter states
  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [exchange, setExchange] = useState(getInitialExchange());
  
  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };
  
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0.00';
    
    const numValue = parseFloat(value);
    const absValue = Math.abs(numValue);
    const sign = numValue < 0 ? '-' : '';
    
    return `${sign}₹${absValue.toFixed(2)}`;
  };
  
  const fetchPnlData = async (newPage, newRowsPerPage, overrideParams = null) => {
    setLoading(true);
    try {
      const params = {
        start_date: formatDate(overrideParams?.startDate || startDate),
        end_date: formatDate(overrideParams?.endDate || endDate),
        page: newPage + 1,
        page_size: newRowsPerPage,
        exchange: overrideParams?.exchange || exchange,
      };
      
      const response = await fetchBillDetailPnl(params);
      if (response && response.data) {
        // Process the bill detail data to match the format needed for PnL display
        const processedData = response.data.map(item => ({
          trade_id: item.BillID,
          bill_id: item.BillID,
          trading_symbol: item.TradingSymbol,
          qty: Math.max(item.BuyQty || 0, item.SellQty || 0),
          buy_amount: item.BuyAmount,
          sell_amount: item.SellAmount,
          buy_avg: item.BuyRate,
          sell_avg: item.SellRate,
          realized_pnl: item.PNL,
          trade_time: item.CreatedAt,
          exchange: item.Exchange,
          narration: item.Narration
        }));
        
        setPnlHistory(processedData);
        setTotalCount(response.total_count || processedData.length);
      } 
    } catch (error) {
      console.error("Error fetching PnL history:", error);
      enqueueSnackbar(error.message || 'Error fetching PnL history', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPnlData(page, rowsPerPage);
  }, []);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchPnlData(newPage, rowsPerPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchPnlData(0, newRowsPerPage);
  };
  
  const handleApplyFilters = () => {
    // Save filter values to localStorage
    localStorage.setItem('pnl_history_startDate', startDate.toISOString());
    localStorage.setItem('pnl_history_endDate', endDate.toISOString());
    localStorage.setItem('pnl_history_exchange', exchange);
    
    setPage(0);
    fetchPnlData(0, rowsPerPage);
  };
  
  const handleResetFilters = () => {
    // Define the reset values first
    const recentBusinessDay = new Date(); // Use today's date
    const threeMonthsBefore = getThreeMonthsBefore(recentBusinessDay);
    const defaultExchange = 'NFO';
    
    // Reset localStorage items
    localStorage.removeItem('pnl_history_startDate');
    localStorage.removeItem('pnl_history_endDate');
    localStorage.removeItem('pnl_history_exchange');
    
    // Set the state with reset values
    setStartDate(threeMonthsBefore);
    setEndDate(recentBusinessDay);
    setExchange(defaultExchange);
    setPage(0);
    
    // Clear current data before fetching
    setPnlHistory([]);
    setTotalCount(0);
    setLoading(true);
    
    // Fetch new data with reset values
    const params = {
      start_date: formatDate(threeMonthsBefore),
      end_date: formatDate(recentBusinessDay),
      page: 1,
      page_size: rowsPerPage,
      exchange: defaultExchange
    };
    
    fetchBillDetailPnl(params)
      .then(response => {
        if (response && response.data) {
          // Process the bill detail data to match the format needed for PnL display
          const processedData = response.data.map(item => ({
            trade_id: item.BillID,
            bill_id: item.BillID,
            trading_symbol: item.TradingSymbol,
            qty: Math.max(item.BuyQty || 0, item.SellQty || 0),
            buy_amount: item.BuyAmount,
            sell_amount: item.SellAmount,
            buy_avg: item.BuyRate,
            sell_avg: item.SellRate,
            realized_pnl: item.PNL,
            trade_time: item.CreatedAt,
            exchange: item.Exchange,
            narration: item.Narration
          }));
          
          setPnlHistory(processedData);
          setTotalCount(response.total_count || processedData.length);
        }
      })
      .catch(error => {
        console.error("Error fetching PnL history:", error);
        enqueueSnackbar(error.message || 'Error fetching PnL history', { 
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 3000
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  // Get color for PnL values
  const getPnlColor = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return theme.palette.text.secondary;
    return numValue > 0 ? theme.palette.success.main : numValue < 0 ? theme.palette.error.main : theme.palette.text.secondary;
  };
  
  // Calculate summary data for P&L
  const summaryData = useMemo(() => {
    if (!pnlHistory || pnlHistory.length === 0) {
      return {
        totalRealizedPnL: 0
      };
    }

    return pnlHistory
      .reduce((acc, item) => {
        acc.totalRealizedPnL += parseFloat(item.realized_pnl || 0);
        return acc;
      }, { totalRealizedPnL: 0 });
  }, [pnlHistory]);

  return (
    <Box sx={{ p: isMobile ? 1 : 2, height: '100%', overflow: 'auto' }}>
      {/* Header with back button and title */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0,
        mb: 3 
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          width: isMobile ? '100%' : 'auto'
        }}>
          <IconButton 
            onClick={() => navigate('/trade-history')}
            sx={{ 
              mr: 1,
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <TimelineIcon sx={{ 
            mr: 1, 
            color: theme.palette.success.main,
            fontSize: isMobile ? 24 : 28
          }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" sx={{ 
            fontWeight: 600,
            color: theme.palette.success.main
          }}>
            Profit & Loss
          </Typography>
        </Box>
      </Box>
      
      {/* Date Range Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Profit and Loss Summary for
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          {format(startDate, 'dd MMM yyyy')} - {format(endDate, 'dd MMM yyyy')}
          <IconButton size="small" sx={{ ml: 1 }} onClick={() => document.getElementById('date-filter').scrollIntoView()}>
            <TimelineIcon color="primary" fontSize="small" />
          </IconButton>
        </Typography>
      </Box>
      
      {/* P&L Summary Card for both mobile and desktop */}
      <Card 
        sx={{ 
          mb: 3, 
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #2A3441 0%, #1F2732 100%)' 
            : 'linear-gradient(135deg, #2C3E50 0%, #1A2330 100%)',
          color: 'white',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ 
          p: isMobile ? 2.5 : 3,
          position: 'relative'
        }}>
          {/* Background decorative elements */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
            zIndex: 0,
            opacity: 0.1
          }}>
            <Box sx={{
              position: 'absolute',
              top: '-5%',
              left: '-5%',
              width: '40%',
              height: '40%',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(76,175,80,0.6) 0%, rgba(76,175,80,0) 70%)',
              display: summaryData.totalRealizedPnL >= 0 ? 'block' : 'none'
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: '-5%',
              right: '-5%',
              width: '40%',
              height: '40%',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(244,67,54,0.6) 0%, rgba(244,67,54,0) 70%)',
              display: summaryData.totalRealizedPnL < 0 ? 'block' : 'none'
            }} />
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            position: 'relative',
            zIndex: 1
          }}>
            {/* Title on the left */}
            <Typography variant="h6" color="rgba(255,255,255,0.85)" sx={{ 
              fontWeight: 600,
              letterSpacing: 0.5,
            }}>
              Net Realised P&L
            </Typography>
            
            {/* Amount on the right */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: summaryData.totalRealizedPnL >= 0 ? '#4CAF50' : '#F44336',
                mr: 0.5
              }}>
                ₹{Math.abs(summaryData.totalRealizedPnL).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </Typography>
              {summaryData.totalRealizedPnL !== 0 && (
                summaryData.totalRealizedPnL > 0 
                  ? <ArrowDropUpIcon sx={{ color: '#4CAF50', fontSize: 28 }} />
                  : <ArrowDropDownIcon sx={{ color: '#F44336', fontSize: 28 }} />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
  
      {/* Filters */}
      <Paper id="date-filter" sx={{ p: isMobile ? 1.5 : 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
        <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
              <DatePicker
                label="From"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size={isMobile ? "small" : "medium"}
                  />
                )}
                inputFormat="dd/MM/yyyy"
                maxDate={endDate}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
              <DatePicker
                label="To"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    size={isMobile ? "small" : "medium"}
                  />
                )}
                inputFormat="dd/MM/yyyy"
                minDate={startDate}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel id="exchange-select-label">Exchange</InputLabel>
              <Select
                labelId="exchange-select-label"
                value={exchange}
                label="Exchange"
                onChange={(e) => setExchange(e.target.value)}
              >
                <MenuItem value="NFO">Derivatives</MenuItem>
                <MenuItem value="MCX">Commodities</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexDirection: isMobile ? 'column' : 'row',
              width: '100%'
            }}>
              <Button 
                variant="contained" 
                startIcon={<SearchIcon />} 
                onClick={handleApplyFilters}
                disabled={loading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Search
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />} 
                onClick={handleResetFilters}
                disabled={loading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Results */}
      <Box sx={{ width: '100%' }}>
        <TableContainer sx={{ 
          maxHeight: isMobile ? 'calc(100vh - 400px)' : 'calc(100vh - 300px)',
          overflow: 'auto'
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : pnlHistory.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No PnL history found for the selected filters
              </Typography>
            </Box>
          ) : (
            <>
              {isMobile ? (
                // Mobile view - Card layout
                <Box sx={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  {pnlHistory.map((item, index) => (
                    <Card
                      key={`${item.trade_id}-${index}`}
                      elevation={1}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      {/* Header with Trading Symbol and Side */}
                      <Box sx={{ 
                        p: 1.5, 
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {item.trading_symbol || item.instrument_id}
                        </Typography>
                        <Chip 
                          label={item.exchange} 
                          size="small" 
                          sx={{ 
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                            fontWeight: 500,
                            fontSize: '0.7rem'
                          }} 
                        />
                      </Box>
                      
                      {/* PnL and Trade Details */}
                      <CardContent sx={{ p: 1.5 }}>
                        {/* PnL Amount with Direction Indicator */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mb: 2,
                          pb: 1.5,
                          borderBottom: `1px solid ${theme.palette.divider}`
                        }}>
                          <Typography variant="body2" color="text.secondary">
                            Realised P&L
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 700,
                              color: getPnlColor(item.realized_pnl),
                              mr: 0.5
                            }}>
                              {formatCurrency(item.realized_pnl)}
                            </Typography>
                            {parseFloat(item.realized_pnl || 0) !== 0 && (
                              parseFloat(item.realized_pnl || 0) > 0 
                                ? <ArrowDropUpIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                                : <ArrowDropDownIcon sx={{ color: theme.palette.error.main, fontSize: 24 }} />
                            )}
                          </Box>
                        </Box>
                        
                        {/* Trade Details in Grid */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                          <Box sx={{ flex: 1, textAlign: 'left' }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Qty
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {item.qty}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Buy Avg.
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.buy_avg > 0 ? `₹${parseFloat(item.buy_avg).toFixed(2)}` : "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Sell Avg.
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.sell_avg > 0 ? `₹${parseFloat(item.sell_avg).toFixed(2)}` : "-"}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                          <Box sx={{ flex: 1, maxWidth: '200px', textAlign: 'left' }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Bill ID
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                              {item.bill_id || "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, maxWidth: '200px', textAlign: 'left' }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Trade Time
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                              {item.trade_time ? format(new Date(item.trade_time), 'dd MMM yyyy, HH:mm:ss') : "-"}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                // Desktop view - Table layout 
                <Table stickyHeader aria-label="sticky table" size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bill ID</TableCell>
                      <TableCell>Trading Symbol</TableCell>
                      <TableCell>Exchange</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Buy Average</TableCell>
                      <TableCell>Sell Average</TableCell>
                      <TableCell>Realized PnL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pnlHistory.map((item, index) => (
                      <TableRow hover tabIndex={-1} key={`${item.trade_id}-${index}`}>
                        <TableCell>{item.bill_id}</TableCell>
                        <TableCell>{item.trading_symbol || item.instrument_id}</TableCell>
                        <TableCell>{item.exchange}</TableCell>
                        <TableCell>{item.qty}</TableCell>
                        <TableCell>{item.buy_avg > 0 ? `₹${parseFloat(item.buy_avg).toFixed(2)}` : "-"}</TableCell>
                        <TableCell>{item.sell_avg > 0 ? `₹${parseFloat(item.sell_avg).toFixed(2)}` : "-"}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ color: getPnlColor(item.realized_pnl), fontWeight: 600 }}>
                              {formatCurrency(item.realized_pnl)}
                            </Typography>
                            {parseFloat(item.realized_pnl || 0) !== 0 && (
                              parseFloat(item.realized_pnl || 0) > 0 
                                ? <ArrowDropUpIcon sx={{ color: theme.palette.success.main }} />
                                : <ArrowDropDownIcon sx={{ color: theme.palette.error.main }} />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </TableContainer>
        
        <Divider />
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={isMobile ? "Rows" : "Rows per page"}
        />
      </Box>
    </Box>
  );
};

export default PnlHistoryDetails; 